<template>
  <div class="p-6">
    <h2 class="mb-4">Config Manager</h2>

    <Card>
      <template #title>
        <div class="flex items-center gap-2">
          <i class="pi pi-cog text-orange-600"></i>
          Configuration Settings
        </div>
      </template>
      <template #content>
        <div v-if="loading && !configData" class="flex items-center justify-center py-8">
          <ProgressSpinner />
          <span class="ml-2">Loading configuration…</span>
        </div>

        <div v-else-if="error && !configData" class="p-4 bg-red-50 border border-red-200 rounded">
          <p class="text-red-800"><strong>Error:</strong> {{ error }}</p>
          <Button
            label="Retry"
            icon="pi pi-refresh"
            class="p-button-sm mt-2"
            @click="loadConfig"
          />
        </div>

        <div v-else class="space-y-6">
          <!-- Minimum Datasets for Aggregated Results -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Minimum Datasets for Aggregated Results
            </label>
            <InputNumber
              v-model="form.minOfDatasetsForAggResults"
              :min="1"
              :max="100"
              showButtons
              buttonLayout="horizontal"
              class="w-full"
              :disabled="loading"
            />
            <p class="text-xs text-gray-500 mt-1">Default: 5</p>
          </div>

          <!-- Minimum Count for Task Completion Percentage -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Minimum Count for Task Completion Percentage
            </label>
            <InputNumber
              v-model="form.minCountForTaskCompletionPercentage"
              :min="1"
              :max="100"
              showButtons
              buttonLayout="horizontal"
              class="w-full"
              :disabled="loading"
            />
            <p class="text-xs text-gray-500 mt-1">Default: 3</p>
          </div>

          <!-- KSS champion minimum participants -->
          <!-- div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              KSS Minimum Participants for Champion Question
            </label>
            <InputNumber
              v-model="form.kssMinParticipantsForChampion"
              :min="2"
              :max="100"
              showButtons
              buttonLayout="horizontal"
              class="w-full"
              :disabled="loading"
            />
            <p class="text-xs text-gray-500 mt-1">Default: 3 — champion question hidden below this count</p>
          </div -->

          <!-- Maximum Active Personal Goals -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Maximum Active Personal Goals
            </label>
            <InputNumber
              v-model="form.maxActivePersonalGoals"
              :min="1"
              :max="100"
              showButtons
              buttonLayout="horizontal"
              class="w-full"
              :disabled="loading"
            />
            <p class="text-xs text-gray-500 mt-1">Default: 3</p>
          </div>

          <!-- Dev Tip Delivery Mode -->
          <div class="field pt-2 border-t border-gray-200">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Dev Tip Delivery Mode
            </label>
            <Select
              v-model="form.devTipDeliveryMode"
              :options="devTipDeliveryModeOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select"
              class="w-full max-w-xs"
              :disabled="loading"
            />
            <p class="text-xs text-gray-500 mt-1">
              <strong>Parallel:</strong> process all dev-tip schemas each run.
              <strong>Alternate:</strong> process one schema per run in rotation.
            </p>
          </div>

          <!-- Link After Assessment options (redirect paths for assessments) -->
          <div class="field">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Link After Assessment options
            </label>
            <p class="text-xs text-gray-500 mb-2">
              Paths shown in the Assessment Manager "Link After Done" dropdown. Each entry has a label and value (path).
            </p>
            <div
              v-for="(opt, index) in form.linkAfterAssessmentOptions"
              :key="index"
              class="flex gap-2 items-center mb-2"
            >
              <input
                v-model="opt.label"
                type="text"
                class="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-sm"
                placeholder="Label"
                :disabled="loading"
              />
              <input
                v-model="opt.value"
                type="text"
                class="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1 text-sm"
                placeholder="Path (e.g. / or /results)"
                :disabled="loading"
              />
              <Button
                icon="pi pi-trash"
                class="p-button-rounded p-button-text p-button-danger"
                :disabled="loading || form.linkAfterAssessmentOptions.length <= 1"
                @click="removeLinkOption(index)"
              />
            </div>
            <Button
              label="Add option"
              icon="pi pi-plus"
              class="p-button-sm p-button-outlined"
              :disabled="loading"
              @click="addLinkOption"
            />
          </div>

          <!-- Action Buttons -->
          <div class="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <Button
              label="Save Changes"
              icon="pi pi-save"
              :disabled="!hasChanges || loading"
              :loading="loading"
              @click="handleSave"
            />
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Card from 'primevue/card';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import Select from 'primevue/select';
import configService from '@/services/configService';

export default {
  name: 'ConfigManager',
  components: {
    Card,
    InputNumber,
    Button,
    ProgressSpinner,
    Select,
  },
  data() {
    const defaultOptions = [
      { label: 'Home', value: '/' },
      { label: 'Reflexion Guideline', value: '/reflexionGuideline' },
    ];
    return {
      form: {
        minOfDatasetsForAggResults: 5,
        minCountForTaskCompletionPercentage: 3,
        // kssMinParticipantsForChampion: 3,
        maxActivePersonalGoals: 3,
        devTipDeliveryMode: null,
        linkAfterAssessmentOptions: JSON.parse(JSON.stringify(defaultOptions)),
      },
      originalForm: {
        minOfDatasetsForAggResults: 5,
        minCountForTaskCompletionPercentage: 3,
        // kssMinParticipantsForChampion: 3,
        maxActivePersonalGoals: 3,
        devTipDeliveryMode: null,
        linkAfterAssessmentOptions: JSON.parse(JSON.stringify(defaultOptions)),
      },
      devTipDeliveryModeOptions: [
        { label: 'Parallel', value: 'parallel' },
        { label: 'Alternate', value: 'alternate' },
      ],
    };
  },
  computed: {
    ...mapState('config', {
      configData: (s) => s.data,
      loading: (s) => s.loading,
      error: (s) => s.error,
    }),
    hasChanges() {
      const form = this.form;
      const orig = this.originalForm;
      if (
        form.minOfDatasetsForAggResults !== orig.minOfDatasetsForAggResults ||
        form.minCountForTaskCompletionPercentage !== orig.minCountForTaskCompletionPercentage ||
        // form.kssMinParticipantsForChampion !== orig.kssMinParticipantsForChampion ||
        form.maxActivePersonalGoals !== orig.maxActivePersonalGoals ||
        form.devTipDeliveryMode !== orig.devTipDeliveryMode
      ) {
        return true;
      }
      if (
        !Array.isArray(form.linkAfterAssessmentOptions) ||
        !Array.isArray(orig.linkAfterAssessmentOptions) ||
        form.linkAfterAssessmentOptions.length !== orig.linkAfterAssessmentOptions.length
      ) {
        return true;
      }
      return form.linkAfterAssessmentOptions.some(
        (opt, i) => {
          const o = orig.linkAfterAssessmentOptions[i];
          return !o || opt.label !== o.label || opt.value !== o.value;
        },
      );
    },
  },
  mounted() {
    this.loadConfig();
  },
  methods: {
    ...mapActions('config', ['fetchConfig', 'updateConfig']),
    async loadConfig() {
      try {
        const [config, devTipModeResponse] = await Promise.all([
          this.fetchConfig(),
          configService.getDevTipDeliveryMode().catch(() => ({ data: {} })),
        ]);
        // fetchConfig returns the config object; dev-tip-delivery-mode returns { data: { devTipDeliveryMode } }
        const devTipData = devTipModeResponse?.data?.data || devTipModeResponse?.data || {};
        const devTipDeliveryMode = devTipData.devTipDeliveryMode || null;

        if (config || devTipData) {
          const defaultOptions = [
            { label: 'Home', value: '/' },
            { label: 'Reflexion Guideline', value: '/reflexionGuideline' },
          ];
          const options = Array.isArray(config.linkAfterAssessmentOptions) && config.linkAfterAssessmentOptions.length > 0
            ? config.linkAfterAssessmentOptions.map((o) => ({ label: o.label || '', value: o.value || '' }))
            : defaultOptions;
          this.form = {
            minOfDatasetsForAggResults: config?.minOfDatasetsForAggResults ?? 5,
            minCountForTaskCompletionPercentage: config?.minCountForTaskCompletionPercentage ?? 3,
            // kssMinParticipantsForChampion: config?.kssMinParticipantsForChampion ?? 3,
            maxActivePersonalGoals: config?.maxActivePersonalGoals ?? 3,
            devTipDeliveryMode,
            linkAfterAssessmentOptions: options,
          };
          this.originalForm = JSON.parse(JSON.stringify(this.form));
        }
      } catch (err) {
        console.error('Failed to load config:', err);
        const defaultOptions = [
          { label: 'Home', value: '/' },
          { label: 'Reflexion Guideline', value: '/reflexionGuideline' },
        ];
        this.form = {
          minOfDatasetsForAggResults: 5,
          minCountForTaskCompletionPercentage: 3,
          // kssMinParticipantsForChampion: 3,
          maxActivePersonalGoals: 3,
          devTipDeliveryMode: null,
          linkAfterAssessmentOptions: JSON.parse(JSON.stringify(defaultOptions)),
        };
        this.originalForm = JSON.parse(JSON.stringify(this.form));
      }
    },
    linkAfterAssessmentOptionsChanged() {
      return JSON.stringify(this.form.linkAfterAssessmentOptions)
        !== JSON.stringify(this.originalForm.linkAfterAssessmentOptions);
    },
    async handleSave() {
      if (!this.hasChanges) return;

      try {
        // PATCH /config/settings only accepts UpdateTenantConfigSettingsDto fields.
        // devTipDeliveryMode must use PATCH /config/dev-tip-delivery-mode (see configService).
        const settingsChanged =
          this.form.minOfDatasetsForAggResults !== this.originalForm.minOfDatasetsForAggResults ||
          this.form.minCountForTaskCompletionPercentage !== this.originalForm.minCountForTaskCompletionPercentage ||
          // this.form.kssMinParticipantsForChampion !== this.originalForm.kssMinParticipantsForChampion ||
          this.form.maxActivePersonalGoals !== this.originalForm.maxActivePersonalGoals ||
          this.linkAfterAssessmentOptionsChanged();
        const devTipModeChanged = this.form.devTipDeliveryMode !== this.originalForm.devTipDeliveryMode;

        if (settingsChanged) {
          const updateData = {
            minOfDatasetsForAggResults: this.form.minOfDatasetsForAggResults,
            minCountForTaskCompletionPercentage: this.form.minCountForTaskCompletionPercentage,
            // kssMinParticipantsForChampion: this.form.kssMinParticipantsForChampion,
            maxActivePersonalGoals: this.form.maxActivePersonalGoals,
            linkAfterAssessmentOptions: this.form.linkAfterAssessmentOptions,
          };
          await this.updateConfig(updateData);
          await this.fetchConfig();
        }

        if (devTipModeChanged && this.form.devTipDeliveryMode) {
          await configService.updateDevTipDeliveryMode(this.form.devTipDeliveryMode);
        }

        // Update original form after successful save
        this.originalForm = JSON.parse(JSON.stringify(this.form));

        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Configuration updated successfully',
          life: 3000,
        });
      } catch (err) {
        console.error('Failed to update config:', err);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: err.response?.data?.message || err.message || 'Failed to update configuration',
          life: 5000,
        });
      }
    },
    addLinkOption() {
      this.form.linkAfterAssessmentOptions.push({ label: '', value: '' });
    },
    removeLinkOption(index) {
      if (this.form.linkAfterAssessmentOptions.length <= 1) return;
      this.form.linkAfterAssessmentOptions.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
}
</style>
